import { ColumnDisplayOptions } from '../containers_v2/map/colorScale';
import axios from 'axios';
import { FieldType } from 'bindings/forms/FieldType';
import { selector } from 'recoil';
import { URL_FOUNDATION } from '../config/keys';
import { FormTemplate } from '../containers_v2/forms/jsonValidator';

// -----------------------[ FIELDS ]----------------------- //
export type Field = {
	id: number,
	name: string,
	data?: object | Array<string>,
	type: FieldType,
	slug: string,
	forms?: string[]
	is_company: boolean,
	is_form: boolean,
	is_additional: boolean,
	is_product: boolean,
	constraint?: object,
	is_promotion: boolean,
	screen?: string,
	// a promotion field computed from the others: the backend runs the
	// formula on every write of the promotion (see promotions/fieldDisplay.ts)
	formula?: string | null,
	// how the value shows, `{}` standing for it: "{} €", "🎯 {}"
	format?: string | null,
	// the colours of the values in the promotion lists, as calculated fields have
	display_options?: ColumnDisplayOptions | null,
}

type AddressField = {
	streetNumber?: string,
	route?: string,
	locality?: string,
	administrativeAreaLevel2?: string,
	administrativeAreaLevel1?: string,
	country?: string,
	postalCode?: string,
	latitude?: number,
	longitude?: number,
}

export type RayonMigration = { 'MultiDimentionField': number } | { 'Single': string }

export type MigrateFormTemplate = {
	history_form: number,
	new_form: number,
	money_fields: number[],
	rayon: RayonMigration,
}

export function isAddressField(field: unknown): field is AddressField {
	return typeof field === 'object' && !!field && 'country' in field;
}

export const AFormFields = selector<Field[]>({
	key: 'AFormFields',
	get: async() => getFields()
});

export async function getFields(): Promise<Field[]> {
	return axios.get(`${URL_FOUNDATION}/api/v2/forms/fields`).then(res => res.data);
}

export async function importFieldsBySlug(fields: Omit<Field, 'id'>[]): Promise<void> {
	return axios.post(`${URL_FOUNDATION}/api/v2/forms/fields/import`, fields).then(res => res.data);
}

export async function migrateFormTemplate(body: MigrateFormTemplate): Promise<void> {
	return axios.post(`${URL_FOUNDATION}/api/v2/forms/templates/migrate`, body).then(res => res.data);
}

// -----------------------[ FORMS ]----------------------- //
type FormTemplateAtom = {
	instanceForms: FormTemplate[],
	templateForms: FormTemplate[],
	allForms: FormTemplate[]
}


export const AFormTemplate = selector<FormTemplateAtom>({
	key: 'AFormTemplate',
	get: async() => getFormTemplates()
});

export function isTemplateForm(form: FormTemplate) {
	return form.screens.length > 0 && form.screens[0].config?.type === 'template';
}

export async function getFormTemplates(body?: { limit?: number, offset?: number }): Promise<FormTemplateAtom> {
	return await axios.post<FormTemplate[]>(
		`${URL_FOUNDATION}/api/v2/forms/templates/query`, body ?? {}
	).then(res => res.data.reduce<FormTemplateAtom>((acc, curr) => {
		if (isTemplateForm(curr)) {
			acc.templateForms.push(curr);
		} else {
			acc.instanceForms.push(curr);
		}
		return acc;
	}, { instanceForms: [], templateForms: [], allForms: res.data }));

}

export async function putFormTemplate(id: number, body: Partial<FormTemplate>): Promise<FormTemplate> {
	return await axios.put(`${URL_FOUNDATION}/api/v2/forms/templates/${id}`, body).then(res => res.data);
}

export async function getLastStartedAtFormInstance(id: number) {
	return await axios.get(`${URL_FOUNDATION}/api/v2/forms/instance/last/${id}`).then(res => res.data);
}
